import { Link, matchPath, useLocation } from "react-router";

import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { type BaseNavigationItem } from "./Sidebar.types";

const STYLE_CONSTANTS = {
  iconBase: "h-6 w-6",
  buttonBase: "flex items-center",
  textBase: "text-text/primary",
};

export const SubNavigationList = ({
  items,
  horizontal = false,
}: {
  items: BaseNavigationItem[];
  horizontal?: boolean;
}) => {
  const location = useLocation();

  const isContentItemActive = (href: string) =>
    matchPath({ path: href.split("?")[0], end: true }, location.pathname) !==
    null;

  return (
    <nav
      className={cnTwMerge(
        horizontal ? "flex items-center gap-1 px-3 py-2" : "space-y-1",
      )}
    >
      {items.map((item) => {
        const isActive = isContentItemActive(item.href);
        const isDisabled = item.disabled ?? false;

        const content = (
          <>
            {isTruthy(item.icon) && (
              <item.icon
                className={cnTwMerge(
                  STYLE_CONSTANTS.iconBase,
                  "mr-3 text-text/secondary",
                )}
              />
            )}
            {item.name}
          </>
        );

        const className = cnTwMerge(
          STYLE_CONSTANTS.buttonBase,
          "px-3 py-2 body-base-medium rounded-companyBorderRadius transition-colors duration-200",
          {
            "bg-background/component": isActive && !isDisabled,
            [STYLE_CONSTANTS.textBase]: isActive && !isDisabled,
            "text-text/secondary hover:bg-background/component hover:text-text/primary":
              !isActive && !isDisabled,
            "opacity-25 cursor-not-allowed pointer-events-none": isDisabled,
            "cursor-default": isActive && !isDisabled,
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-default":
              !isDisabled,
          },
        );

        const linkElement = (
          <Link
            aria-disabled={isDisabled}
            className={className}
            key={item.name}
            to={item.href}
          >
            {content}
          </Link>
        );

        if (item.tooltip) {
          return (
            <Tooltip
              content={item.tooltip}
              key={item.name}
              providerProps={{ delayDuration: 0 }}
            >
              <span className="cursor-not-allowed">{linkElement}</span>
            </Tooltip>
          );
        }

        return linkElement;
      })}
    </nav>
  );
};
